// Salary export by payment mode — the month's bank transfer / cash
// disbursement instruction. Verified by scripts/check-payroll-export.mjs.
//
// Two layers, kept apart on purpose:
//   planPaymentModeExport()  pure: stored month data -> what goes on which
//                            sheet, what needs attention, the totals. This
//                            is what the pre-download notice shows, and it
//                            is exactly what the workbook contains.
//   writePaymentModeWorkbook()  ExcelJS rendering of that plan.
//
// Sources (decision 08-09-2026): everyone with calculated pay for the month,
// wherever the money record lives — payslips rows (status ok) AND
// payroll_attendance rows with status blocked / excluded (Cash). One money
// record per person per month is an invariant; a person found in both is an
// inconsistency and the export REFUSES rather than guess.
//
// Blocked employees are INCLUDED on their payment-mode sheet (they are owed
// the calculated figure) with the row shaded, and listed again on the
// "Check before paying" sheet with their blocker reasons. Blocked employees
// with no computable figure cannot be placed and appear on that sheet only,
// marked missing — the file is then known to be incomplete.
//
// The Cash sheet has no account-number or IFSC column: it is a disbursement
// list, not a bank file, and an always-blank column invites someone to fill
// it in. The two bank sheets carry both — a transfer instruction without the
// IFSC sends someone to look each one up by hand. A missing IFSC is placed
// and marked exactly like a missing account number.

export const PAYMENT_MODES = ['Bank Account', 'Net Banking', 'Cash'];
export const ATTENTION_SHEET = 'Check before paying';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const fmtMonthLong = (ym) => {
  if (!/^\d{4}-\d{2}$/.test(ym || '')) return ym || '';
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

// Payment type as stored -> sheet name, or null when it cannot be placed
// (blank, or a legacy value outside the three the Employees form offers).
export const paymentMode = (raw) => {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'bank account') return 'Bank Account';
  if (v === 'net banking') return 'Net Banking';
  if (v === 'cash') return 'Cash';
  return null;
};

const hasFigure = (v) => v !== null && v !== undefined && Number.isFinite(Number(v));
const isBlank = (v) => String(v ?? '').trim() === '';

// Rupees for the notice: en-IN grouping, paise only when present.
export const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// slips:      payslips rows for the month — { id, employee_id, net_pay, employees: { id, employee_code, name, payment_type, account_number, ifsc_code } | null }
// attendance: payroll_attendance rows for the month with status blocked/excluded
//             — { id, employee_id, muster_employee_id, muster_name, status, net_pay, flags }
// employees:  employees rows for the attendance rows' employee_ids
//             — { id, employee_code, name, payment_type, account_number, ifsc_code }
// Throws on an inconsistency the export must not paper over.
export function planPaymentModeExport({ month, slips, attendance, employees }) {
  if (!/^\d{4}-\d{2}$/.test(month || '')) throw new Error(`Not a month: "${month}"`);
  const empById = new Map((employees || []).map((e) => [e.id, e]));

  // Unified person records.
  const people = [];
  const seen = new Map(); // employee_id -> where it was found
  for (const s of slips || []) {
    const e = s.employees;
    if (!e) throw new Error(`Payslip ${s.id} has no employee record attached — the export cannot place it. Nothing was exported.`);
    if (seen.has(e.id)) throw new Error(`${e.name} (${e.employee_code}) has more than one payslip for ${fmtMonthLong(month)}. Nothing was exported — investigate before paying.`);
    seen.set(e.id, 'payslip');
    people.push({
      employee_id: e.id, code: e.employee_code, name: e.name,
      payment_type: e.payment_type, account_number: e.account_number, ifsc_code: e.ifsc_code,
      net_pay: hasFigure(s.net_pay) ? Number(s.net_pay) : null,
      status: 'ok', flags: [],
    });
  }
  for (const a of attendance || []) {
    const e = a.employee_id ? empById.get(a.employee_id) : null;
    if (a.employee_id && seen.has(a.employee_id)) {
      const who = e ? `${e.name} (${e.employee_code})` : a.employee_id;
      throw new Error(`${who} has BOTH a payslip and a no-slip attendance record for ${fmtMonthLong(month)} — two money records for one person. Nothing was exported; re-upload the month to repair it.`);
    }
    if (a.employee_id) seen.set(a.employee_id, 'attendance');
    if (a.employee_id && !e) throw new Error(`Attendance row for ${a.muster_name || a.muster_employee_id} points at an employee that could not be loaded. Nothing was exported.`);
    people.push({
      employee_id: a.employee_id || null,
      code: e ? e.employee_code : a.muster_employee_id,
      name: e ? e.name : (a.muster_name || a.muster_employee_id),
      payment_type: e ? e.payment_type : null,
      account_number: e ? e.account_number : null,
      ifsc_code: e ? e.ifsc_code : null,
      net_pay: hasFigure(a.net_pay) ? Number(a.net_pay) : null,
      status: a.status, flags: Array.isArray(a.flags) ? a.flags : [],
    });
  }

  const byName = (x, y) => String(x.name || '').localeCompare(String(y.name || ''), undefined, { sensitivity: 'base' })
    || String(x.code || '').localeCompare(String(y.code || ''));
  people.sort(byName);

  const sheets = Object.fromEntries(PAYMENT_MODES.map((m) => [m, []]));
  const attention = []; // { person, mode, issue, kind }
  const add = (person, mode, kind, issue) => attention.push({ person, mode, kind, issue });

  for (const p of people) {
    const mode = paymentMode(p.payment_type);
    const blockers = p.flags.filter((f) => f.level === 'blocker').map((f) => f.message);
    if (p.net_pay === null) {
      add(p, mode, 'no_figure', p.status === 'blocked'
        ? `No calculated figure (${blockers.join(' ') || 'blocked at upload'}) — MISSING from this file; fix the employee record and re-upload the month.`
        : 'No net pay stored for this month — MISSING from this file.');
      continue;
    }
    if (!mode) {
      add(p, mode, 'unplaceable', `Payment type "${p.payment_type || ''}" is not Bank Account / Net Banking / Cash — cannot be placed on a sheet; set it in the Employees tab. Net pay ${rupees(p.net_pay)} is NOT in any sheet total.`);
      continue;
    }
    const isCash = mode === 'Cash';
    const row = {
      sl: sheets[mode].length + 1, name: p.name, code: p.code,
      account_number: isCash ? null : (p.account_number ?? ''),
      ifsc: isCash ? null : String(p.ifsc_code ?? '').trim().toUpperCase(),
      net_pay: p.net_pay, blocked: p.status === 'blocked',
      no_account: !isCash && isBlank(p.account_number),
      no_ifsc: !isCash && isBlank(p.ifsc_code),
      negative: p.net_pay < 0,
    };
    sheets[mode].push(row);
    if (row.blocked) add(p, mode, 'blocked', `Blocked at upload — included on the ${mode} sheet (row ${row.sl}) at the calculated figure. ${blockers.join(' ')}`);
    if (row.no_account) add(p, mode, 'no_account', `No account number on the employee record — the ${mode} row cannot be paid as is.`);
    if (row.no_ifsc) add(p, mode, 'no_ifsc', `No IFSC on the employee record — the bank needs it for the ${mode} row (row ${row.sl}); add it in the Employees tab.`);
    if (row.negative) add(p, mode, 'negative_net', `Net pay is negative (${rupees(p.net_pay)}) — fixed deductions exceed earnings; resolve with HR.`);
  }

  const perMode = Object.fromEntries(PAYMENT_MODES.map((m) => [m, {
    count: sheets[m].length,
    total: sheets[m].reduce((t, r) => t + r.net_pay, 0),
  }]));
  const placed = PAYMENT_MODES.reduce((t, m) => t + perMode[m].count, 0);
  const total = PAYMENT_MODES.reduce((t, m) => t + perMode[m].total, 0);
  const missing = attention.filter((a) => a.kind === 'no_figure' || a.kind === 'unplaceable');
  const monthLabel = fmtMonthLong(month);

  return {
    month,
    monthLabel,
    filename: `Salary-${monthLabel.replace(' ', '-')}-by-payment-mode.xlsx`,
    sheets,
    perMode,
    attention,
    placed,             // people on the three mode sheets
    total,              // sum of the three mode sheets
    missing,            // attention entries NOT in any sheet total
    complete: missing.length === 0,
    people: people.length,
  };
}

// Renders the plan to an .xlsx ArrayBuffer. ExcelJS is imported lazily —
// it is a heavy dependency and this runs once a month.
export async function writePaymentModeWorkbook(plan) {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Alfresco Grand — Booking Management';

  const NAVY = 'FF0A1628';
  const NAVY_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
  const AMBER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
  const RED_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
  const THIN = { style: 'thin', color: { argb: 'FFD0D5DD' } };
  const BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };
  const MONEY = '#,##0.00';

  const header = (ws, title, note, cols) => {
    const r1 = ws.addRow([title]);
    r1.height = 24;
    r1.getCell(1).font = { bold: true, size: 12, color: { argb: NAVY } };
    const r2 = ws.addRow([note]);
    r2.height = 14;
    r2.getCell(1).font = { italic: true, size: 9, color: { argb: 'FF666666' } };
    const r3 = ws.addRow(cols.map((c) => c.header));
    r3.height = 18;
    cols.forEach((c, i) => {
      const cell = r3.getCell(i + 1);
      cell.fill = NAVY_FILL;
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      cell.border = BORDER;
      cell.alignment = { horizontal: c.align || 'left', vertical: 'middle' };
    });
    ws.columns = cols.map((c) => ({ width: c.width }));
    ws.views = [{ state: 'frozen', ySplit: 3 }];
  };

  for (const mode of PAYMENT_MODES) {
    const ws = wb.addWorksheet(mode);
    const isCash = mode === 'Cash';
    const cols = isCash
      ? [{ header: 'Sl No', width: 8, align: 'right' }, { header: 'Employee Name', width: 34 }, { header: 'Net Pay', width: 16, align: 'right' }]
      : [{ header: 'Sl No', width: 8, align: 'right' }, { header: 'Employee Name', width: 34 }, { header: 'Account Number', width: 24 }, { header: 'IFSC', width: 16 }, { header: 'Net Pay', width: 16, align: 'right' }];
    const rows = plan.sheets[mode];
    const blockedCount = rows.filter((r) => r.blocked).length;
    header(
      ws,
      `Alfresco Grand — Salary for ${plan.monthLabel} — ${mode}`,
      `${rows.length} employee${rows.length === 1 ? '' : 's'} · net pay as calculated at upload${blockedCount ? ` · ${blockedCount} shaded row${blockedCount === 1 ? '' : 's'}: blocked at upload — see "${ATTENTION_SHEET}"` : ''}${isCash ? ' · paid in cash, no account number' : ' · a red cell is a missing account number or IFSC'}`,
      cols,
    );
    if (!rows.length) {
      const r = ws.addRow([`No employees paid by ${mode} for ${plan.monthLabel}.`]);
      r.getCell(1).font = { italic: true, color: { argb: 'FF666666' } };
      continue;
    }
    for (const row of rows) {
      const values = isCash ? [row.sl, row.name, row.net_pay] : [row.sl, row.name, row.account_number === null ? '' : String(row.account_number), row.ifsc || '', row.net_pay];
      const r = ws.addRow(values);
      const netCol = isCash ? 3 : 5;
      r.getCell(1).alignment = { horizontal: 'right' };
      r.getCell(netCol).numFmt = MONEY;
      r.getCell(netCol).alignment = { horizontal: 'right' };
      if (!isCash) {
        // Text on purpose: account numbers keep leading zeros and never
        // become 1.2E+11.
        r.getCell(3).numFmt = '@';
        r.getCell(3).alignment = { horizontal: 'left' };
        if (row.no_account) r.getCell(3).fill = RED_FILL;
        r.getCell(4).numFmt = '@';
        r.getCell(4).alignment = { horizontal: 'left' };
        if (row.no_ifsc) r.getCell(4).fill = RED_FILL;
      }
      for (let c = 1; c <= values.length; c++) {
        r.getCell(c).border = BORDER;
        const missingCell = !isCash && ((c === 3 && row.no_account) || (c === 4 && row.no_ifsc));
        if (row.blocked && !missingCell) r.getCell(c).fill = AMBER_FILL;
      }
      if (row.negative) r.getCell(netCol).fill = RED_FILL;
    }
    const totalRow = ws.addRow(isCash ? ['', 'Total', plan.perMode[mode].total] : ['', 'Total', '', '', plan.perMode[mode].total]);
    totalRow.font = { bold: true };
    const netCol = isCash ? 3 : 5;
    totalRow.getCell(netCol).numFmt = MONEY;
    totalRow.getCell(netCol).alignment = { horizontal: 'right' };
    for (let c = 1; c <= netCol; c++) totalRow.getCell(c).border = BORDER;
  }

  // Attention sheet — always present so the layout is stable.
  {
    const ws = wb.addWorksheet(ATTENTION_SHEET);
    const cols = [
      { header: 'Employee Name', width: 34 }, { header: 'Code', width: 10 }, { header: 'Sheet', width: 16 },
      { header: 'Net Pay', width: 16, align: 'right' }, { header: 'What to check', width: 110 },
    ];
    header(
      ws,
      `Alfresco Grand — Salary for ${plan.monthLabel} — ${ATTENTION_SHEET}`,
      plan.attention.length
        ? `${plan.attention.length} item${plan.attention.length === 1 ? '' : 's'}. Rows marked MISSING are in NO sheet total${plan.complete ? '' : ' — this file is incomplete until they are resolved'}.`
        : 'Nothing to check — every employee is placed on a sheet with a figure, and every bank row has an account number and an IFSC.',
      cols,
    );
    for (const a of plan.attention) {
      const r = ws.addRow([a.person.name, a.person.code, a.mode || '—', a.person.net_pay ?? '', a.issue]);
      r.getCell(4).numFmt = MONEY;
      r.getCell(4).alignment = { horizontal: 'right' };
      r.getCell(5).alignment = { wrapText: true, vertical: 'top' };
      for (let c = 1; c <= 5; c++) r.getCell(c).border = BORDER;
      if (a.kind === 'no_figure' || a.kind === 'unplaceable') r.getCell(5).fill = RED_FILL;
      else if (a.kind === 'blocked') r.getCell(5).fill = AMBER_FILL;
    }
  }

  return wb.xlsx.writeBuffer();
}
